// Guest-pass owner UI models (TA3). Mirror the Account-service view models
// (GuestPassViewModels.cs) 1:1 — the issuance dialog + pass list consume these.
// Guest passes are served by the ACCOUNT base URL (apiBaseUrl); there is no
// Customers proxy for them (see guestPassesApi.ts).

/** Manual issuance request. The owning company is taken from the caller server-side,
 *  never sent from the client. `expiresAt` is computed from the duration picker
 *  (now + N days); the server clamps at 7 days regardless. */
export interface IssueGuestPass {
  machineInternalNumber: string;
  guestEmail: string;
  guestName: string;
  validFrom?: string;
  expiresAt?: string;
  visitPurpose?: string;
  /** EMAIL-LANG-WIRING: UI locale (en/sk/cs) for the guest invite email; server normalizes, unknown -> EN. */
  language?: string;
}

/** Backend Status strings (GuestPassService.cs:317-325). */
export type GuestPassStatus = "Active" | "Expired" | "Revoked";

/** `GuestPass.source` — mirrors backend `GuestPassSourceCode`.
 *  Demo = 3 is the DEMO_USER_TYPE_SPEC pass-row marker (B2). */
export const GuestPassSource = {
  Auto: 1,
  Manual: 2,
  Demo: 3,
} as const;

/** Issuer-facing pass row (issue response + list rows). Carries NO secret material
 *  EXCEPT `pinCode`, which the demo-mint response may return once — see below. */
export interface GuestPass {
  id: string;
  machineInternalNumber: string;
  guestEmail: string;
  guestName: string;
  validFrom: string;
  expiresAt: string;
  revokedAt?: string;
  source: number;
  firstRedeemedAt?: string;
  redeemCount: number;
  status: string;
  /** POST RESPONSES ONLY — never on a list row, never on any GET.
   *
   *  Three carriers, all POST (GP-MENU §3.1 "show-once property"):
   *    • the demo-mint response          (`POST /guest-passes/demo`)
   *    • the guest regenerate response   (`POST /guest-passes/{id}/regenerate-pin`)
   *    • the demo regenerate response    (`POST /guest-passes/demo/{id}/regenerate-pin`)
   *  The earlier "DEMO ONLY" wording is stale — regeneration returns the PIN for
   *  BOTH sources, because the screen is the only handoff channel on that path
   *  (no email is sent on any source).
   *
   *  The owner's flow is "read the PIN aloud on a Teams call", so the handoff
   *  panel displays it. The field stays OPTIONAL so the UI compiles and runs
   *  against a backend that does not yet return it: present → the PIN is shown;
   *  absent → the panel renders its missing-PIN fault state instead of crashing.
   *
   *  NEVER read this off a list row and NEVER persist it — it is display-once
   *  material held in memory for the duration of the open dialog. The slice keeps
   *  it in `lastDemoPass` / `lastRegeneratedPass` ONLY, never in `passes`. */
  pinCode?: string;
  /** DEMO ONLY. Server-side session cap in hours (`DemoAccessSettings.DemoSessionCapHours`).
   *
   *  SEC finding: a redeemed demo session ends at `min(redeemedAt + cap, expiresAt)`,
   *  which is much SHORTER than the pass window in `expiresAt`. The two are shown as
   *  separate facts in the handoff panel — `expiresAt` is when the PIN stops working,
   *  the cap is how long ONE sign-in lasts.
   *
   *  The frontend NEVER assumes this number. If the server does not send it, the UI
   *  states that a cap exists without quoting a value. Do NOT hardcode a default. */
  sessionCapHours?: number;
}
